import { defineStore } from 'pinia'

const TOAST_CLOSE_ANIMATION_MS = 200
const toastRemovalTimers = new Map<number, ReturnType<typeof setTimeout>>()

export interface ToastAction {
    label: string
    to: string
}

export interface ToastItem {
    id: number
    title: string
    action?: ToastAction
    open: boolean
}

interface ToastStoreState {
    items: ToastItem[]
    nextId: number
}

export const useToastStore = defineStore('toastStore', {
    state: (): ToastStoreState => ({
        items: [],
        nextId: 0,
    }),
    actions: {
        show(payload: Omit<ToastItem, 'id' | 'open'>): number {
            const id = this.nextId + 1
            this.nextId = id

            const removalTimer = toastRemovalTimers.get(id)

            if (removalTimer) {
                clearTimeout(removalTimer)
                toastRemovalTimers.delete(id)
            }

            this.items.push({
                id,
                open: true,
                ...payload,
            })

            return id
        },
        showCartAddedToast(): number {
            return this.show({
                title: 'Товар добавлен в корзину',
                action: {
                    label: 'Перейти в корзину',
                    to: '/cart/',
                },
            })
        },
        setOpen(id: number, open: boolean): void {
            const item = this.items.find((toast) => toast.id === id)

            if (!item) {
                return
            }

            item.open = open

            if (!open) {
                const previousTimer = toastRemovalTimers.get(id)

                if (previousTimer) {
                    clearTimeout(previousTimer)
                }

                const removalTimer = setTimeout(() => {
                    this.remove(id)
                    toastRemovalTimers.delete(id)
                }, TOAST_CLOSE_ANIMATION_MS)

                toastRemovalTimers.set(id, removalTimer)
            }
        },
        remove(id: number): void {
            const removalTimer = toastRemovalTimers.get(id)

            if (removalTimer) {
                clearTimeout(removalTimer)
                toastRemovalTimers.delete(id)
            }

            this.items = this.items.filter((toast) => toast.id !== id)
        },
        closeAll(): void {
            this.items.forEach((toast) => {
                this.setOpen(toast.id, false)
            })
        },
        clear(): void {
            toastRemovalTimers.forEach((timer) => clearTimeout(timer))
            toastRemovalTimers.clear()
            this.items = []
        },
    },
})
